import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as ExcelJS from 'exceljs';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { useAuth } from '../../components/auth/AuthContext';
import { accountingService } from '../../services/api/accountingService';
import { AccountingEntry, AccountingConcept, CONCEPT_LABELS } from '../../types/accounting';
import { logger } from '../../utils/logger';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../components/grids/TradeGrid.css';
import './Positions.css';
import '../../styles/FilterButtons.css';

const ALL_CONCEPTS: AccountingConcept[] = [
  'NEW_TRADE',
  'VALUATION',
  'VALUATION_REVERSAL',
  'EXPIRY',
  'MODIFICATION_CANCELLED',
  'UNWIND',
];

/** Format YYYY-MM-DD to DD/MM/YYYY for display */
const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return isoDate;
};

/** Format number with locale-appropriate separators */
const formatAmount = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '';
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Get first day of current month as YYYY-MM-DD */
const getFirstOfMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

/** Get last day of current month as YYYY-MM-DD */
const getLastOfMonth = (): string => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
};

/**
 * Trade number sort key — decomposes trade numbers into a numeric tuple for correct ordering.
 * Examples:
 *   12345         → [12345, 0, 0]
 *   SPL-12345.2   → [12345, 0, 2]   (SPL sorts after parent via suffix)
 *   INT-12345.1.1 → [12345, 1, 1]
 * Pattern from CommodityTradesGrid.
 */
function tradeNumberSortKey(tn: string): [number, number, number] {
  if (!tn) return [Infinity, 0, 0];
  // INT-12345.1.2
  const intMatch = tn.match(/^INT-(\d+)\.(\d+)\.(\d+)$/);
  if (intMatch) {
    return [parseInt(intMatch[1], 10), parseInt(intMatch[2], 10), parseInt(intMatch[3], 10)];
  }
  // SPL-12345.2
  const splMatch = tn.match(/^SPL-(\d+)\.(\d+)$/);
  if (splMatch) {
    return [parseInt(splMatch[1], 10), 0, parseInt(splMatch[2], 10)];
  }
  // Plain number
  const num = parseInt(tn, 10);
  if (!isNaN(num)) return [num, 0, 0];
  return [Infinity, 0, 0];
}

const tradeNumberComparator = (a: string, b: string): number => {
  const ka = tradeNumberSortKey(a);
  const kb = tradeNumberSortKey(b);
  return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
};

const AccountingReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;

  // Filters — pending values (changed by user but not yet applied)
  const [pendingDateFrom, setPendingDateFrom] = useState(getFirstOfMonth);
  const [pendingDateTo, setPendingDateTo] = useState(getLastOfMonth);
  // Applied values (used for the actual API call)
  const [dateFrom, setDateFrom] = useState(getFirstOfMonth);
  const [dateTo, setDateTo] = useState(getLastOfMonth);
  const [conceptFilter, setConceptFilter] = useState<string>('all');

  // Data
  const [entries, setEntries] = useState<AccountingEntry[]>([]);
  const [loading, setLoading] = useState(false);

  // Selection
  const [selectedEntries, setSelectedEntries] = useState<any[]>([]);
  const [selectAllTrigger, setSelectAllTrigger] = useState(0);
  const [clearAllTrigger, setClearAllTrigger] = useState(0);

  // Grid ref for select/clear all
  const gridRef = React.useRef<AgGridReact>(null);

  // Track whether pending dates differ from applied dates (show OK button)
  const datesDirty = pendingDateFrom !== dateFrom || pendingDateTo !== dateTo;

  // AG Grid locale
  const agLocale = useMemo(() => {
    if (i18n.language?.startsWith('es')) return AG_GRID_LOCALE_ES;
    if (i18n.language?.startsWith('pt')) return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  // ============================================
  // Data Fetching
  // ============================================

  const fetchEntries = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const params: any = {
        dateFrom,
        dateTo,
      };
      if (conceptFilter !== 'all') {
        params.concept = conceptFilter;
      }
      const response = await accountingService.getEntries(clientId, params);
      if (response.success && response.data) {
        setEntries(response.data);
      } else {
        setEntries([]);
      }
    } catch (error) {
      logger.error('Failed to load accounting entries:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, dateFrom, dateTo, conceptFilter]);

  // Auto-load on mount
  useEffect(() => {
    fetchEntries();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when concept filter changes (concept toggles apply immediately)
  useEffect(() => {
    if (entries.length > 0 || conceptFilter !== 'all') {
      fetchEntries();
    }
  }, [conceptFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply pending dates and fetch
  const handleDateApply = useCallback(() => {
    setDateFrom(pendingDateFrom);
    setDateTo(pendingDateTo);
  }, [pendingDateFrom, pendingDateTo]);

  // Trigger fetch when applied dates change (except on mount, which is handled separately)
  const mountedRef = React.useRef(false);
  useEffect(() => {
    if (mountedRef.current) {
      fetchEntries();
    } else {
      mountedRef.current = true;
    }
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Concepts that appear in the current data
  // ============================================

  const visibleConcepts = useMemo(() => {
    const present = new Set(entries.map(e => e.concept));
    return ALL_CONCEPTS.filter(c => present.has(c));
  }, [entries]);

  // ============================================
  // Row Data (enrich with concept label)
  // ============================================

  const rowData = useMemo(() => entries.map(e => ({
    ...e,
    conceptLabel: t(`accounting.concepts.${e.concept}`, CONCEPT_LABELS[e.concept] || e.concept),
  })), [entries, t]);

  // ============================================
  // Column Definitions
  // ============================================

  const conceptBadgeRenderer = (p: any) => {
    if (!p.value) return null;
    return (
      <span style={{
        padding: '0.1rem 0.45rem',
        borderRadius: '4px',
        fontSize: '0.78rem',
        background: 'rgba(52,152,219,0.15)',
        color: 'var(--accent-blue)',
        whiteSpace: 'nowrap',
      }}>
        {p.value}
      </span>
    );
  };

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: t('accountingReport.columns.entryDate', 'Entry Date'),
      field: 'entryDate',
      sortable: true,
      filter: true,
      sort: 'asc' as const,
      sortIndex: 0,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      headerName: t('accountingReport.columns.concept', 'Concept'),
      field: 'conceptLabel',
      sortable: true,
      filter: true,
      cellRenderer: conceptBadgeRenderer,
    },
    {
      headerName: t('accountingReport.columns.tradeNumber', 'Trade #'),
      field: 'tradeNumber',
      sortable: true,
      filter: true,
      sort: 'asc' as const,
      sortIndex: 1,
      cellStyle: { fontFamily: 'monospace', fontWeight: '500' },
      comparator: tradeNumberComparator,
    },
    {
      headerName: t('accountingReport.columns.account', 'Account'),
      field: 'accountNumber',
      sortable: true,
      filter: true,
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('accountingReport.columns.accountName', 'Account Name'),
      field: 'accountName',
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accountingReport.columns.costCentre', 'Cost Centre'),
      field: 'costCentre',
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accountingReport.columns.debit', 'Debit'),
      field: 'debitAmount',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accountingReport.columns.credit', 'Credit'),
      field: 'creditAmount',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accountingReport.columns.currency', 'CCY'),
      field: 'currency',
      minWidth: 62,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accountingReport.columns.rule', 'Rule'),
      field: 'ruleName',
      sortable: true,
      filter: true,
      tooltipField: 'ruleDescription',
      cellStyle: { fontSize: '0.8rem', color: 'var(--text-secondary)' },
    },
    {
      headerName: t('accountingReport.columns.description', 'Description'),
      field: 'description',
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accountingReport.columns.batch', 'Batch'),
      field: 'batchId',
      sortable: true,
      filter: true,
      valueFormatter: (p) => p.value ? p.value.substring(0, 8) : '',
      tooltipValueGetter: (p) => p.value || '',
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // ============================================
  // Export headers — locale-specific (match grid column names)
  // ============================================

  const getExportHeaders = useCallback(() => [
    t('accountingReport.columns.entryDate', 'Entry Date'),
    t('accountingReport.columns.concept', 'Concept'),
    t('accountingReport.columns.tradeNumber', 'Trade #'),
    t('accountingReport.columns.account', 'Account'),
    t('accountingReport.columns.accountName', 'Account Name'),
    t('accountingReport.columns.costCentre', 'Cost Centre'),
    t('accountingReport.columns.debit', 'Debit'),
    t('accountingReport.columns.credit', 'Credit'),
    t('accountingReport.columns.currency', 'CCY'),
    t('accountingReport.columns.rule', 'Rule'),
    t('accountingReport.columns.description', 'Description'),
    t('accountingReport.columns.batch', 'Batch'),
  ], [t]);

  // ============================================
  // Selection
  // ============================================

  const handleSelectionChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    setSelectedEntries(api.getSelectedRows());
  }, []);

  useEffect(() => {
    if (selectAllTrigger > 0) {
      gridRef.current?.api?.selectAll();
    }
  }, [selectAllTrigger]);

  useEffect(() => {
    if (clearAllTrigger > 0) {
      gridRef.current?.api?.deselectAll();
    }
  }, [clearAllTrigger]);

  // ============================================
  // Export
  // ============================================

  const buildExportRow = (entry: any) => [
    formatDate(entry.entryDate),
    entry.conceptLabel || CONCEPT_LABELS[entry.concept as AccountingConcept] || entry.concept,
    entry.tradeNumber || '',
    entry.accountNumber || '',
    entry.accountName || '',
    entry.costCentre || '',
    entry.debitAmount,
    entry.creditAmount,
    entry.currency || '',
    entry.ruleName || '',
    entry.description || '',
    entry.batchId || '',
  ];

  const handleExportCsv = useCallback(() => {
    if (selectedEntries.length === 0) return;

    const headers = getExportHeaders();
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const rows = selectedEntries.map(entry => buildExportRow(entry).map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accounting_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedEntries, getExportHeaders]);

  const handleExportExcel = useCallback(async () => {
    if (selectedEntries.length === 0) return;

    const headers = getExportHeaders();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Accounting Report');

    // Header row
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF2D2D2D' },
      };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });

    // Data rows
    for (const entry of selectedEntries) {
      const row = buildExportRow(entry);
      // Convert debit/credit to numbers for Excel formatting (indices 6, 7)
      [6, 7].forEach(i => {
        if (row[i] != null) row[i] = Number(row[i]) || row[i];
      });
      worksheet.addRow(row);
    }

    // Number formats for debit/credit columns (1-indexed in ExcelJS)
    const numFmt2 = '#,##0.00';
    worksheet.getColumn(7).numFmt = numFmt2;  // Debit
    worksheet.getColumn(8).numFmt = numFmt2;  // Credit

    // Auto-fit column widths
    worksheet.columns.forEach((col) => {
      const header = col.header as string || '';
      col.width = Math.max(header.length + 2, 12);
    });

    // Freeze header row
    worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accounting_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedEntries, getExportHeaders]);

  // ============================================
  // Clear Filters
  // ============================================

  const handleClearFilters = useCallback(() => {
    const from = getFirstOfMonth();
    const to = getLastOfMonth();
    setPendingDateFrom(from);
    setPendingDateTo(to);
    setDateFrom(from);
    setDateTo(to);
    setConceptFilter('all');
  }, []);

  // ============================================
  // Render
  // ============================================

  return (
    <div className="positions-container">
      <div className="positions-header">
        <h1>{t('accountingReport.title', 'Accounting Report')}</h1>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input
            type="date"
            className="date-filter-input"
            value={pendingDateFrom}
            onChange={(e) => setPendingDateFrom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleDateApply(); }}
          />
          <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>–</span>
          <input
            type="date"
            className="date-filter-input"
            value={pendingDateTo}
            onChange={(e) => setPendingDateTo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleDateApply(); }}
          />
          {datesDirty && (
            <button
              onClick={handleDateApply}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#e2e8f0',
                padding: '2px 10px',
                fontSize: '12px',
                height: '28px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              OK
            </button>
          )}
        </div>
      </div>

      <div className="positions-filters">
        <div className="filter-group" style={{ minWidth: 'auto' }}>
          <label>{t('accountingReport.concept', 'Concept')}</label>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className={`positions-select-all-btn${conceptFilter === 'all' ? ' positions-filter-active' : ''}`}
              onClick={() => setConceptFilter('all')}
            >
              {t('accountingReport.allConcepts', 'All')}
            </button>
            {visibleConcepts.map(concept => (
              <button
                key={concept}
                className={`positions-select-all-btn${conceptFilter === concept ? ' positions-filter-active' : ''}`}
                onClick={() => setConceptFilter(concept)}
              >
                {t(`accounting.concepts.${concept}`, CONCEPT_LABELS[concept])}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={handleClearFilters}
          className="action-btn clear-btn"
        >
          {t('accountingReport.clearFilters', 'Clear Filters')}
        </button>
        <button
          className="positions-select-all-btn"
          onClick={() => setSelectAllTrigger(n => n + 1)}
        >
          {t('positions.selectAll')}
        </button>
        <button
          className="positions-select-all-btn"
          onClick={() => setClearAllTrigger(n => n + 1)}
        >
          {t('positions.clearAll')}
        </button>
        <button
          className="positions-select-all-btn positions-export-btn"
          onClick={handleExportCsv}
          disabled={selectedEntries.length === 0}
          title={selectedEntries.length === 0 ? t('accountingReport.exportCsvHint', 'Select entries to export') : undefined}
        >
          {t('accountingReport.exportCsv', 'Export CSV')}
        </button>
        <button
          className="positions-select-all-btn positions-export-btn"
          onClick={handleExportExcel}
          disabled={selectedEntries.length === 0}
          title={selectedEntries.length === 0 ? t('accountingReport.exportExcelHint', 'Select entries to export') : undefined}
        >
          {t('accountingReport.exportExcel', 'Export Excel')}
        </button>
      </div>

      <div className="positions-content chart-collapsed">
        <div className="positions-grid-wrapper">
          {loading ? (
            <div className="ag-theme-alpine-dark trade-grid loading-state">
              <div className="grid-spinner" />
            </div>
          ) : (
          <div className="ag-theme-alpine-dark trade-grid" style={{ width: '100%', height: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 80,
                filterParams: {
                  buttons: ['reset'],
                  textMatcher: ({ value, filterText, filterOption }: { value: string; filterText: string; filterOption: string }) => {
                    const normalize = (s: string) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                    const nVal = normalize(value);
                    const nFilter = normalize(filterText);
                    switch (filterOption) {
                      case 'contains': return nVal.includes(nFilter);
                      case 'notContains': return !nVal.includes(nFilter);
                      case 'equals': return nVal === nFilter;
                      case 'notEqual': return nVal !== nFilter;
                      case 'startsWith': return nVal.startsWith(nFilter);
                      case 'endsWith': return nVal.endsWith(nFilter);
                      default: return nVal.includes(nFilter);
                    }
                  }
                }
              }}
              sortingOrder={['asc', 'desc']}
              rowSelection="multiple"
              onSelectionChanged={handleSelectionChanged}
              animateRows={true}
              localeText={agLocale}
              overlayNoRowsTemplate={`<span>${t('accountingReport.noEntries', 'No entries found for the selected filters.')}</span>`}
            />
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountingReport;
